import PageHero from "@/components/site/PageHero";
import Reveal from "@/components/site/Reveal";
import { useT } from "@/i18n";

const DEPT_IMG =
  "https://customer-assets.emergentagent.com/job_contemporary-gallery/artifacts/3ipfu8kr_dept2.png";

export default function OurVenues() {
  const t = useT();
  const v = t.pages.venues;

  return (
    <div data-testid="venues-page" className="bg-white">
      <PageHero eyebrow={v.eyebrow} title={v.title} subtitle={v.subtitle} image={DEPT_IMG} />

      {/* Intro / Headline */}
      <section className="py-24 md:py-32" data-testid="venues-intro">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <Reveal className="lg:col-span-5">
            <p className="eyebrow">{v.milanoEyebrow}</p>
            <h2 className="heading-display mt-4 text-3xl sm:text-4xl lg:text-5xl uppercase text-black">
              {v.milanoTitle1}
              <br />
              <span className="text-[#C20E1A] italic">{v.milanoTitle2}</span>
            </h2>
          </Reveal>
          <Reveal className="lg:col-span-7" delay={120}>
            <p className="text-base sm:text-lg text-black/80 leading-relaxed">{v.milanoP1}</p>
            <p className="mt-6 text-base sm:text-lg text-black/80 leading-relaxed">{v.milanoP2}</p>
          </Reveal>
        </div>
      </section>

      {/* Hero image (large editorial) */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14">
          <Reveal>
            <div className="relative w-full aspect-[16/9] overflow-hidden bg-black/5">
              <img
                src={DEPT_IMG}
                alt={v.milanoTitle1}
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="venues-hero-image"
              />
            </div>
            <p className="mt-5 text-xs uppercase tracking-[0.3em] text-black/50">{v.caption}</p>
          </Reveal>
        </div>
      </section>

      {/* Three pillars */}
      <section className="bg-black text-white py-24 md:py-32" data-testid="venues-pillars">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
          <Reveal className="max-w-3xl mb-16 md:mb-20">
            <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.5em] text-[#C20E1A] font-bold">
              {v.pillarsEyebrow}
            </p>
            <h2 className="heading-display mt-5 text-3xl sm:text-4xl lg:text-5xl uppercase text-white">
              {v.pillarsTitle1} <span className="text-[#C20E1A] italic">{v.pillarsTitle2}</span>
            </h2>
            <p className="mt-6 text-base lg:text-lg text-white/70 leading-relaxed">
              {v.pillarsIntro}
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10">
            {v.pillars.map((p, i) => (
              <Reveal
                key={p.n}
                delay={i * 90}
                className="bg-black p-8 md:p-10 lg:p-12 flex flex-col gap-6"
              >
                <p className="text-5xl lg:text-6xl font-bold italic text-[#C20E1A] tracking-tight">
                  {p.n}
                </p>
                <h3 className="text-xl lg:text-2xl uppercase font-bold tracking-tight text-white">
                  {p.title}
                </h3>
                <p className="text-base text-white/70 leading-relaxed">{p.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Stats / Highlights */}
      <section className="bg-[#F5F5F5] py-24 md:py-32" data-testid="venues-highlights">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
          <Reveal className="text-center mb-14 md:mb-20">
            <p className="text-[10px] uppercase tracking-[0.5em] text-[#C20E1A] font-bold">
              {v.highlightsEyebrow}
            </p>
            <h2 className="heading-display mt-5 text-3xl sm:text-4xl lg:text-5xl uppercase text-black">
              {v.highlightsTitle1} <span className="text-[#C20E1A] italic">{v.highlightsTitle2}</span>
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-y-12 gap-x-6 lg:gap-x-10 mb-20">
            {v.highlights.map((h, i) => (
              <Reveal
                key={h.label}
                delay={i * 90}
                className="text-center sm:border-l sm:border-black/15 sm:first:border-l-0 px-3 lg:px-6"
              >
                <p className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold italic tracking-tight text-[#C20E1A] leading-none">
                  {h.value}
                  <span className="not-italic font-bold">{h.suffix}</span>
                </p>
                <p className="mt-5 text-[10px] sm:text-[11px] uppercase tracking-[0.4em] text-black/60 font-bold">
                  {h.label}
                </p>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
              <p className="lg:col-span-5 eyebrow lg:pt-2">{v.designedEyebrow}</p>
              <div className="lg:col-span-7 text-base sm:text-lg text-black/80 leading-relaxed space-y-6">
                <p>{v.designedP1}</p>
                <p>{v.designedP2}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
